import { Dispatch, FC, SetStateAction, useEffect } from 'react';
import { useField, useForm } from 'react-final-form';
import { Link } from 'components/Link';
import { formatNumber, formatNumberAsCurrency, getPathWithRef, scrollToTop } from 'helpers/utils';
import { H4, UtilityText } from 'components/Typography';
import theme from 'config/theme';
import { PaymentsData } from 'store/payments/types';
import {
  getDisplayTextFromRecurringInterval,
  getIsCheckoutMonthly,
  getOfflineReviewHelperText,
  getPaymentMethodTypeDisplay,
  usePaymentOptions,
} from './utils';
import { Tip } from './Tip';
import { getUserState } from 'selectors/user';
import { OathRecurringInterval, PaymentMethod, PaymentMethodType } from 'store/pay/types';
import { trackPixel } from 'helpers/pixel';
import { useAppSelector } from 'helpers/hooks';
import { getIsInStop2End, getIsPageStop2End } from 'helpers/stop2end';
import classNames from 'classnames';
import { useExperiment, useStatsigClient } from '@statsig/react-bindings';
import { mixpanelClient } from 'helpers/mixpanel';

const TIP_COPY_EXPERIMENT_NAME = 'tip_header_v3';

// Fallback defaults for tip header text and subheader text if Statsig data unavailable
const HEADER_TEXT = 'Your donation goes to the race or cause, not to us.';
const SUB_HEADER_TEXT =
  'Oath never sells your data or spams your inbox. Join the 80% of Oath donors who tip to keep our analysis free.';

// F4D tipping variant
const HEADER_TEXT_VARIANT_2 = "Would you consider supporting Oath's giving platform?";
const SUB_HEADER_TEXT_VARIANT_2 =
  'Oath puts donors first by protecting you from spam and has a 0% platform fee. We rely on support from donors like you to keep our platform running.';

const getPaymentDisplay = (
  paymentType: PaymentMethod,
  token: string,
  paymentOptions: PaymentOptions,
  ccNumber?: string,
  achAccountNumber?: string,
  isPledge?: boolean,
  pledgeType?: PaymentMethodType
) => {
  if (isPledge) {
    let pledgeTypeDisplay = pledgeType ? getPaymentMethodTypeDisplay(pledgeType) : 'Pledge';
    pledgeTypeDisplay = pledgeTypeDisplay.charAt(0).toUpperCase() + pledgeTypeDisplay.slice(1);
    return pledgeTypeDisplay;
  }
  const paymentTypeDisplay = paymentType === 'ach' ? 'Bank Account' : 'Credit Card';
  const paymentOptionKey = paymentType === 'ach' ? 'achPayments' : 'ccPayments';
  const selectedPayment = paymentOptions[paymentOptionKey].find(
    payment => payment.payment_authorization_token === token
  );
  const savedPaymentLast4 =
    selectedPayment && selectedPayment.payment_authorization_display_name.slice(-4);
  let newPaymentLast4 = '';
  if (paymentType === 'credit_card' && ccNumber) {
    newPaymentLast4 = ccNumber.slice(-4);
  } else if (achAccountNumber) {
    newPaymentLast4 = achAccountNumber.slice(-4);
  }

  return `${paymentTypeDisplay} ${savedPaymentLast4 || newPaymentLast4}`;
};

interface PaymentOptions {
  achPayments: PaymentsData[];
  ccPayments: PaymentsData[];
}
interface Props {
  backToDonatePath: string;
  children?: React.ReactNode;
  donationAmount: number;
  isTippingVariant2: boolean;
  onBackToDonatePage: () => void;
  onTipChange?: (tip: number) => void;
  onView?: () => void;
  numCandidates: number;
  setActivePageIndex: Dispatch<SetStateAction<number>>;
  isCondensed?: boolean;
  singleLine?: boolean;
  isPledge?: boolean;
  pledgeType?: PaymentMethodType;
  isRecurring?: boolean;
  recurringInterval?: OathRecurringInterval;
  hideTip?: boolean;
}

export const Review: FC<Props> = ({
  backToDonatePath,
  children,
  donationAmount,
  isTippingVariant2,
  onBackToDonatePage,
  onTipChange,
  onView,
  numCandidates,
  setActivePageIndex,
  isCondensed = false,
  singleLine = false,
  isPledge = false,
  pledgeType = 'wire',
  isRecurring = false,
  recurringInterval = 'month',
  hideTip = false,
}) => {
  const form = useForm();
  const {
    ach_account_number: achAccountNumber,
    cc_number: ccNumber,
    default_token: paymentToken,
    email,
    first_name: firstName,
    last_name: lastName,
    payment_type: paymentType,
  } = form.getState().values;
  const {
    input: { value: tip },
  } = useField('tip', { subscription: { value: true } });
  const paymentOptions = usePaymentOptions();
  const userState = useAppSelector(getUserState);
  const experimentTippingCopy = useExperiment(TIP_COPY_EXPERIMENT_NAME);
  const { logEvent: statsigLogEvent } = useStatsigClient();

  useEffect(() => {
    mixpanelClient.register({
      tipping_copy_experiment_group_name: experimentTippingCopy.groupName,
    });
  }, [experimentTippingCopy.groupName]);

  useEffect(() => {
    if (onTipChange) {
      onTipChange(tip);
    }
  }, [tip]);

  useEffect(() => {
    scrollToTop();
    trackPixel('donationSuccess');
    statsigLogEvent('view_checkout_review');
    if (onView) {
      onView();
    }
  }, []);

  return (
    <>
      {children}
      <div className="checkout-review-grid-wrapper">
        <div
          className={classNames({
            'checkout-review-grid-item-1': !isTippingVariant2,
            'checkout-review-grid-item-2': isTippingVariant2,
          })}
        >
          <div className="contribution-summary">
            <span className="body-text">Contribution Summary</span>
            <Link
              to={getPathWithRef(backToDonatePath)}
              onClick={onBackToDonatePage}
              $color={theme.shadows.black(0.5)}
            >
              <span className="body-small-text color-inherit">Edit</span>
            </Link>
          </div>
          <div className="row justify-between">
            <div className="column">
              <H4>{formatNumberAsCurrency(Number(donationAmount) + Number(tip))}</H4>
              <UtilityText>
                {getIsCheckoutMonthly() || isRecurring
                  ? `Recurring ${getDisplayTextFromRecurringInterval(
                      recurringInterval
                    )} contribution`
                  : 'One-time contribution'}
              </UtilityText>
              {!hideTip && (
                <UtilityText>Including {formatNumberAsCurrency(tip)} tip to Oath</UtilityText>
              )}
            </div>
            <div className="column">
              <span className="h4-text">
                {formatNumber(numCandidates)} recipient{numCandidates !== 1 && 's'}
              </span>
            </div>
          </div>
          {!isCondensed && (
            <>
              <div className="personal-details">
                <div className="row justify-between">
                  <span className="body-text">Personal Details</span>
                  <Link
                    onClick={() => setActivePageIndex(0)}
                    $color={theme.shadows.black(0.5)}
                    type="button"
                  >
                    <span className="body-small-text color-inherit">Edit</span>
                  </Link>
                </div>
                <div className="column" data-dd-privacy="mask">
                  <span className="body-small-text">{`${firstName} ${lastName}`}</span>
                  <span className="body-small-text">{email}</span>
                </div>
              </div>
              <div>
                <div className="row justify-between">
                  <span className="body-text">Payment Method</span>
                  <Link
                    {...(isPledge
                      ? { to: getPathWithRef(backToDonatePath) }
                      : { onClick: () => setActivePageIndex(1) })}
                    $color={theme.shadows.black(0.5)}
                    type="button"
                  >
                    <span className="body-small-text color-inherit">Edit</span>
                  </Link>
                </div>
                <span className="body-small-text">
                  {getPaymentDisplay(
                    paymentType,
                    paymentToken,
                    paymentOptions,
                    ccNumber,
                    achAccountNumber,
                    isPledge,
                    pledgeType
                  )}
                </span>
              </div>
            </>
          )}
        </div>
        {!hideTip && (
          <div
            className={classNames('oath-powered-by-donor-tips', {
              'checkout-review-grid-item-1': isTippingVariant2,
              'checkout-review-grid-item-2': !isTippingVariant2,
            })}
          >
            <span
              className={classNames('body-text', {
                'semi-bold': isTippingVariant2,
                'color-oathBlue': isTippingVariant2,
              })}
            >
              {isTippingVariant2
                ? HEADER_TEXT_VARIANT_2
                : experimentTippingCopy.get('tip_header_text', HEADER_TEXT)}
            </span>
            <span className="body-small-text">
              {isTippingVariant2
                ? SUB_HEADER_TEXT_VARIANT_2
                : experimentTippingCopy.get('tip_subheader_text', SUB_HEADER_TEXT)}
            </span>
            <Tip
              donationAmount={donationAmount}
              isVariant2={isTippingVariant2}
              tipAmount={tip}
              singleLine={singleLine}
            />
          </div>
        )}
      </div>
      {isPledge && (
        <div className="wire-review">
          <hr />
          <span className="body-small-text">{getOfflineReviewHelperText(pledgeType)}</span>
        </div>
      )}
      {getIsInStop2End(email, userState?.data?.id) && (
        <div className="stop2end-disclaimer">
          <span className="utility-text">
            By clicking below, you authorize 51C Collaborative, Inc. (DBA "Oath") to unsubscribe
            your logged in email address from unwanted political campaign and committee
            communications on your behalf.
          </span>
        </div>
      )}
      {getIsPageStop2End() && !getIsInStop2End(email, userState?.data?.id) && (
        <div className="stop2end-disclaimer">
          <span className="utility-text">
            WARNING: You are not logged in. If you are intending to opt-in for the STOP2END campaign
            and have Oath help unsubscribe you from political lists, please go back to Step 2 and
            click "Sign in to give faster".
          </span>
        </div>
      )}
    </>
  );
};
